import { tt } from '@agos/shared'

/**
 * *Doména zákazníckej stránky* - `SAAS.md`-side half of `BACKEND.md` §10A.359, `FRONTEND.md` §9.702, owner O84.
 *
 * **The operator:** *"Sereď web domain for password reset links, yes. They have custom, its not working? If no,
 * add it as a setting."* The reset link, the e-mail footer and the referral link all read the franchise's
 * **primary** registered domain (`FranchiseRegistry.findPublicIdentity`), and so do the customer branch picker
 * and the proxy provisioning. Until now only the creation wizard and the setup script could set it, and this
 * screen showed no domain at all - so nobody could see which address a customer's link opens, let alone choose
 * it.
 *
 * **A choice among the registered domains, never free text**: the reset page posts back to `/api` on the host
 * it was opened on, and a host with no registry row resolves no franchise, so a link to an unregistered domain
 * cannot work. The server refuses one by name; this screen does not offer one.
 */

export interface RegisteredDomain {
  domain: string
  isPrimary: boolean
}

/** The franchise row's `domains`, as `GET /super-admin/franchises` answers them. Anything else is skipped. */
export function registeredDomainsOf(row: Record<string, unknown> | null): RegisteredDomain[] {
  const raw = row?.domains
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry): RegisteredDomain[] => {
    if (typeof entry !== 'object' || entry === null) return []
    const one = entry as Record<string, unknown>
    const domain = typeof one.domain === 'string' ? one.domain.trim() : ''
    if (domain === '') return []
    return [{ domain, isPrimary: one.isPrimary === true }]
  })
}

/** The domain customer links use now: the primary row, or `null` when none is marked. */
export function customerDomainOf(domains: readonly RegisteredDomain[]): string | null {
  return domains.find((one) => one.isPrimary)?.domain ?? null
}

/**
 * What the section says about the current state - the three a franchise can be in.
 *
 * With no primary the server falls back to the first domain that does not look like a manager's (§10A.359), so
 * the sentence says a choice is missing rather than naming a guess this screen would have to re-derive.
 */
export function customerDomainNote(domains: readonly RegisteredDomain[]): string {
  if (domains.length === 0) {
    return tt(
      'Franšíza nemá žiadnu doménu, takže odkazy v e-mailoch zákazníkom vedú na predvolenú adresu platformy. Doménu pridáte cez franchise-setup.sh (Domény).',
    )
  }
  const current = customerDomainOf(domains)
  if (current === null) {
    return tt(
      'Žiadna doména nie je označená ako zákaznícka, takže odkazy vedú na prvú, ktorá nevyzerá ako manažérska. Zvoľte ju.',
    )
  }
  return `${tt('Odkazy v e-mailoch zákazníkom vedú na')} https://${current}`
}

/** The one key `PATCH /super-admin/franchises/{id}` takes for this - nothing else of the row is touched. */
export function primaryDomainPayload(chosen: string): { primaryDomain: string } {
  return { primaryDomain: chosen }
}

/** Whether there is anything to save: a domain chosen, and not the one already primary. */
export function primaryDomainChanged(
  chosen: string,
  domains: readonly RegisteredDomain[],
): boolean {
  return chosen !== '' && chosen !== customerDomainOf(domains)
}
